import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { VictoryChart, VictoryLine, VictoryTooltip, VictoryVoronoiContainer } from 'victory';
import calendar from '../../../../assets/images/icons/calendar.svg';
import download from '../../../../assets/images/icons/download.svg';
import info from '../../../../assets/images/icons/info.svg';
import NoData from '../../../../components/NoData';
import OpacityAnimation from '../../../../components/OpacityAnimation';
import ExportXlsx from '../../../../utils/ExportXlsx';
import getTodayDate from '../../../../utils/getTodayDate';
import { DadosNotImpl, PeriodType, SelectedDashboardType } from '../types';
import ChangePeriodModal from './ChangePeriodModal';
import { Card } from './PreAdmDashboard/components/styles';
// import { useState } from 'react';

interface IImpAutCard {
  autImpData: DadosNotImpl[];
  loadImpAutData: (month?: number, year?: number) => Promise<void>;
  periodBeingSelectedAutImp: PeriodType;
  setPeriodBeingSelectedAutImp: Dispatch<SetStateAction<PeriodType>>;
  setSelectedPeriodAutImp: Dispatch<SetStateAction<PeriodType>>;
  selectedPeriodAutImp: PeriodType;
  selectedDashboard: SelectedDashboardType;
}

export default function ImpAutCard({
  autImpData,
  loadImpAutData,
  periodBeingSelectedAutImp,
  setPeriodBeingSelectedAutImp,
  setSelectedPeriodAutImp,
  selectedPeriodAutImp,
  selectedDashboard,
}: IImpAutCard ) {
  const [changePeriodModalShow, setChangePeriodModalShow] = useState(false);

  const largestNumber = Math.max(...autImpData?.map((x) => ([
    x.notificaPorConsulta,
    x.totImplantacoes,
    x.erroEnvio,
    x.totEnviosEsperado
  ])).flat() || [0]);
  const smallestNumber = Math.min(...autImpData?.map((x) => ([
    x.notificaPorConsulta,
    x.totImplantacoes,
    x.erroEnvio,
    x.totEnviosEsperado
  ])).flat() || [0]);

  const monthsMap: { [key: number]: string } = useMemo(() => (
    {
      1: 'Janeiro',
      2: 'Fevereiro',
      3: 'Março',
      4: 'Abril',
      5: 'Maio',
      6: 'Junho',
      7: 'Julho',
      8: 'Agosto',
      9: 'Setembro',
      10: 'Outubro',
      11: 'Novembro',
      12: 'Dezembro',
    }
  ), []);

  const downloadRel = useCallback(() => {
    const data = autImpData.map((item) => ({
      Dia: item.dia,
      'Total de consultas com envio de otificação': item.notificaPorConsulta,
      'Total de Implantações': item.totImplantacoes,
    }));

    const today = getTodayDate().replaceAll('/', '-');

    ExportXlsx({data, filename: `Efetividade das notificações (Imp. Automática) ${monthsMap[selectedPeriodAutImp.month]} de ${selectedPeriodAutImp.year} - Gerado em ${today}`});
  }, [autImpData, monthsMap, selectedPeriodAutImp.month, selectedPeriodAutImp.year]);

  return (
    <>
      <Card>
        <div className="card-title">
          <div>IMPLANTAÇÕES AUTOMATIZADAS-Efetividade das notificações - quantidade X dias
            {' '}
            <strong style={{ marginLeft: '5px' }}>
              {' '}
              {monthsMap[selectedPeriodAutImp.month]} de {selectedPeriodAutImp.year}
            </strong>
            <img src={info} alt="" className='primaryColor info' title='Acompanhamento da efetividade do serviço de implantação automático, informando a quantidade de envios de e-mails que deveriam ser realizados, os envios que não foram realizados, os envios realizados, e a quantidade de implantações realizadas no dia destes envios.'/>
          </div>
          <div>
            <div>
              <img src={calendar} alt="changeMonth" title="Alterar mês de visualização" onClick={() => setChangePeriodModalShow(true)} />
            </div>
            <div>
              <img src={download} alt="download" title="Baixar relatório excel" onClick={() => downloadRel()} />
            </div>
          </div>
        </div>

        {(!!autImpData && autImpData.length > 0) && (
          <>
            <OpacityAnimation>
              <div className="card-main-graph-line">
                <VictoryChart
                  height={100}
                  width={300}
                  padding={{
                    top: 15, bottom: 30, left: 25, right: 20,
                  }}
                  maxDomain={{ y: largestNumber * 1.05 }}
                  minDomain={{ y: smallestNumber * 0.95 }}
                  containerComponent={(
                    <VictoryVoronoiContainer
                      labels={({ datum }) => `${datum.label}`}
                      labelComponent={(
                        <VictoryTooltip
                          constrainToVisibleArea
                          centerOffset={{ x: 5, y: 40 }}
                          style={{ fill: 'black', fontSize: 6 }}
                          flyoutHeight={20}
                          flyoutWidth={70}
                        // flyoutStyle={{ fontSize: 10 }}
                        />
                      )}
                    />
                  )}
                >
                  <VictoryLine
                    style={{
                      data: { stroke: '#4aafff', strokeWidth: 1 },
                      parent: { border: 'none' },
                    }}
                    data={autImpData.map((data) => (
                      { x: `${data.dia}`, y: data.totEnviosEsperado, label: `${data.totEnviosEsperado} ${selectedDashboard === 'base' ? 'otimizados' : 'consultas'}\ncom envios esperados\n(dia ${data.dia})` }
                    ))}
                    interpolation="natural"
                    animate={{
                      duration: 3000,
                      onLoad: { duration: 5000 },
                    }}
                  />
                  <VictoryLine
                    style={{
                      data: { stroke: '#FC5050', strokeWidth: 1 },
                      parent: { border: 'none' },
                    }}
                    data={autImpData.map((data) => (
                      { x: `${data.dia}`, y: data.erroEnvio, label: `${data.erroEnvio} ${selectedDashboard === 'base' ? 'otimizados' : 'consultas'}\ncom erro no envio\n(dia ${data.dia})` }
                    ))}
                    interpolation="natural"
                    animate={{
                      duration: 3000,
                      onLoad: { duration: 5000 },
                    }}
                    labels={() => ''}
                  />
                  <VictoryLine
                    style={{
                      data: { stroke: '#da7c0f', strokeWidth: 1 },
                      parent: { border: 'none' },
                    }}
                    data={autImpData.map((data) => (
                      { x: `${data.dia}`, y: data.notificaPorConsulta, label: `${data.notificaPorConsulta} ${selectedDashboard === 'base' ? 'otimizados' : 'consultas'}\ncom envio de notificação\n(dia ${data.dia})` }
                    ))}
                    interpolation="natural"
                    animate={{
                      duration: 3000,
                      onLoad: { duration: 5000 },
                    }}
                    labels={() => ''}
                  />
                  <VictoryLine
                    style={{
                      data: { stroke: '#3ea76a', strokeWidth: 1.2 },
                      parent: { border: 'none' },
                    }}
                    data={autImpData.map((data) => (
                      { x: `${data.dia}`, y: data.totImplantacoes, label: `${data.totImplantacoes} ${selectedDashboard === 'base' ? 'otimizados' : 'consultas'}\n${selectedDashboard === 'base' ? 'implantados' : 'implantadas'}\n(dia ${data.dia})` }
                    ))}
                    interpolation="natural"
                    animate={{
                      duration: 3000,
                      onLoad: { duration: 5000 },
                    }}
                    labels={() => ''}
                  />
                </VictoryChart>
              </div>
            </OpacityAnimation>
          </>
        )}

        {(!autImpData || autImpData.length === 0) && (
          <NoData
            icon="emptyBox"
            label={(
              <>
              Não há dados disponíveis sobre a efetividade das notificações no mês informado.
              </>
            )}
          />
        )}
      </Card>

      <ChangePeriodModal
        periodBeingSelectedAutImp={periodBeingSelectedAutImp}
        setPeriodBeingSelectedAutImp={setPeriodBeingSelectedAutImp}
        setSelectedPeriodAutImp={setSelectedPeriodAutImp}
        changePeriodModalShow={changePeriodModalShow}
        setChangePeriodModalShow={setChangePeriodModalShow}
        customAction={loadImpAutData}
      />
    </>
  );
}
